import { useState, ChangeEvent, FormEvent, FC, useEffect, useMemo } from "react";
import { Box, TextField, Button, Typography, Paper, Stack, IconButton, Grid, Avatar, Drawer, Accordion, AccordionSummary, AccordionDetails, Divider } from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteIcon from "@mui/icons-material/Delete";
import { GetFileByIdResults, useGetCurrentFile } from "~/queries/useGetFileById";
import { GetCommentsByFileIdResults, useGetCurrentFileComments } from "~/queries/useGetCommentsByFileId";
import { useCreateComment } from "~/queries/useCreateComment";
import { IMAGE_FILE_EXTENSIONS, VIDEO_FILE_EXTENSIONS } from "~served/configs";
import { $dayjs, DATETIMESTRING_FORMAT } from "~/libs/dayjs";
import { EditNote, ExpandCircleDown, Reply } from "@mui/icons-material";
import { useMeCache } from "~/queries/useMe";
import { useBoolean } from "usehooks-ts";
import { useUpdateComment } from "~/queries/useUpdateComment";
import { alpha } from "@mui/system/colorManipulator";
import toast from "react-hot-toast";
import { CommentAttachmentInput, FILE_UPLOAD_TYPE } from "~/types/__generated/gql/graphql";
import axios from "axios";
import { useGetCurrentCompanyCache } from "~/queries/useGetCurrentCompany";
import { useGetFileUploadUrl } from "~/queries/useGetFileUploadUrl";
interface FileAttachment {
  file: File;
  preview: string | null;
  type: string;
}
interface CommentDisplay {
  comment: GetCommentsByFileIdResults[0];
  replies: GetCommentsByFileIdResults;
}
const CommentSection: FC<{
  isAttachmentEnabled: boolean | undefined;
}> = ({
  isAttachmentEnabled
}) => {
  const {
    data: file
  } = useGetCurrentFile();
  const {
    data: comments = []
  } = useGetCurrentFileComments();
  const {
    data: me
  } = useMeCache();
  const {
    data: company
  } = useGetCurrentCompanyCache();
  const getFileUploadUrl = useGetFileUploadUrl();
  const createComment = useCreateComment();
  const [updatedComment, setUpdatedComment] = useState<GetCommentsByFileIdResults[0] | undefined>();
  const {
    value: isEditCommentSliderOpen,
    setTrue: openEditCommentSlider,
    setFalse: closeEditCommentSlider
  } = useBoolean(false);
  const {
    value: isReplyCommentSliderOpen,
    setTrue: openReplyCommentSlider,
    setFalse: closeReplyCommentSlider
  } = useBoolean(false);
  const [input, setInput] = useState<string>("");
  const [files, setFiles] = useState<FileAttachment[]>([]);
  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files ?? []);
    const newFiles: FileAttachment[] = selectedFiles.map(file => {
      const type = file.type;
      const preview = type.startsWith("image/") || type.startsWith("video/") ? URL.createObjectURL(file) : null;
      return {
        file,
        preview,
        type
      };
    });
    setFiles(prev => [...prev, ...newFiles]);
  };
  const handleRemoveFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!input.trim() && files.length === 0) return;
    if (!file) return;
    await toast.promise(
    // eslint-disable-next-line no-async-promise-executor
    new Promise(async (resolve, reject) => {
      try {
        const fileAttachments: CommentAttachmentInput[] = [];
        if (files.length > 0) {
          for (const f of files) {
            const {
              name,
              size,
              type
            } = f.file;
            const nameArr = name.split(".");
            const {
              uploadUrl,
              filePath
            } = await getFileUploadUrl.mutateAsync({
              type: FILE_UPLOAD_TYPE.COMMENT_ATTACHMENT,
              id: company!._id,
              extension: nameArr[nameArr.length - 1],
              mimeType: type
            });
            await axios.put(uploadUrl, f.file, {
              headers: {
                "Content-Type": type
              },
              transformRequest: data => data
            });
            fileAttachments.push({
              name: name,
              extension: nameArr[nameArr.length - 1],
              size,
              url: filePath
            });
          }
        }
        await createComment.mutateAsync({
          input: {
            text: input.trim(),
            file: file._id,
            ...(fileAttachments.length > 0 ? {
              attachments: fileAttachments
            } : undefined)
          }
        });
        resolve(null);
      } catch (e) {
        reject(e instanceof Error ? e : new Error("Something went wrong"));
      }
    }), {
      loading: "Uploading comment...",
      success: "Comment uploads successfully",
      error: "Failed to upload comment"
    });
    setInput("");
    setFiles([]);
  };
  const handleEditComment = (id: string) => {
    const currentComment = comments.find(c => c._id === id);
    if (!currentComment) return;
    setUpdatedComment(currentComment);
    openEditCommentSlider();
  };
  const handleReplyComment = (id: string) => {
    const currentComment = comments.find(c => c._id === id);
    if (!currentComment) return;
    setUpdatedComment(currentComment);
    openReplyCommentSlider();
  };
  const commentsDisplay = useMemo(() => {
    const displays: CommentDisplay[] = [];
    const sortedComments = [...comments].reverse();
    for (const comment of sortedComments) {
      if (!comment.comment) {
        displays.push({
          comment: comment,
          replies: []
        });
      } else {
        const index = displays.findIndex(d => d.comment._id === comment.comment);
        if (index === -1) {
          displays.push({
            comment: comment,
            replies: []
          });
        } else {
          displays[index].replies.push(comment);
        }
      }
    }
    return displays.reverse();
  }, [comments]);
  return <>
			<Box sx={{
      margin: "2rem auto",
      p: 2
    }} data-sentry-element="Box" data-sentry-source-file="index.tsx">
				<Typography variant="h5" gutterBottom data-sentry-element="Typography" data-sentry-source-file="index.tsx">
					Comments
				</Typography>

				<Box component="form" onSubmit={handleSubmit} noValidate data-sentry-element="Box" data-sentry-source-file="index.tsx">
					<TextField label="Write a comment" multiline fullWidth rows={3} value={input} onChange={e => setInput(e.target.value)} variant="outlined" data-sentry-element="TextField" data-sentry-source-file="index.tsx" />

					{/* Preview selected files */}
					<Grid container spacing={2} sx={{
          mt: 2
        }} data-sentry-element="Grid" data-sentry-source-file="index.tsx">
						{files.map((f, idx) => <Grid item xs={12} sm={6} key={idx}>
								<Box sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              border: "1px solid #ccc",
              borderRadius: 2,
              p: 1
            }}>
									{/* File preview */}
									{f.type.startsWith("image/") && f.preview && <img src={f.preview} alt={f.file.name} style={{
                width: 60,
                height: 60,
                objectFit: "cover"
              }} />}

									{f.type.startsWith("video/") && f.preview && <video width="80" height="60" controls>
											<source src={f.preview} type={f.type} />
										</video>}

									{/* File name display */}
									<Box sx={{
                flexGrow: 1
              }}>
										<Typography variant="body2" sx={{
                  wordBreak: "break-word"
                }} title={f.file.name}>
											{f.file.name}
										</Typography>
									</Box>

									<IconButton onClick={() => handleRemoveFile(idx)} color="error">
										<DeleteIcon />
									</IconButton>
								</Box>
							</Grid>)}
					</Grid>

					<Box sx={{
          mt: 2,
          display: "flex",
          justifyContent: "right",
          gap: 2
        }} data-sentry-element="Box" data-sentry-source-file="index.tsx">
						{isAttachmentEnabled && <Button component="label" variant="outlined" startIcon={<AttachFileIcon />}>
								Attach File
								<input type="file" hidden multiple onChange={handleFileChange} />
							</Button>}
						<Button type="submit" variant="contained" disabled={!input.trim() && files.length === 0} data-sentry-element="Button" data-sentry-source-file="index.tsx">
							Comment
						</Button>
					</Box>
				</Box>
				{/* Render Comments */}
				<Stack spacing={2} sx={{
        mt: 4
      }} data-sentry-element="Stack" data-sentry-source-file="index.tsx">
					{commentsDisplay.map(c => <>
							<Box key={c.comment._id} sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 2
          }}>
								{/* Avatar */}
								<Avatar sx={{
              bgcolor: "primary.main",
              color: "primary.contrastText"
            }}>
									{c.comment._user.firstName.charAt(0).toUpperCase() || "U"}
								</Avatar>

								{/* Chat Bubble */}
								<Paper elevation={1} sx={{
              py: 1,
              px: 2,
              borderRadius: 1,
              backgroundColor: theme => alpha(theme.palette.action.disabled, 0.05),
              maxWidth: "90%",
              width: "90%"
            }}>
									{/* Message text */}
									<Typography variant="body2" sx={{
                mb: c.comment.attachments?.length ? 1 : 0
              }}>
										{`${c.comment._user.lastName} ${c.comment._user.firstName} - ${$dayjs(c.comment?.updatedAt).format(DATETIMESTRING_FORMAT)}`}
									</Typography>
									{/* Message text */}
									{c.comment.text && <Typography variant="body1" sx={{
                mb: c.comment.attachments?.length ? 1 : 0
              }}>
											{c.comment.text}
										</Typography>}

									{/* Attachments */}
									<Grid container spacing={1}>
										{c.comment.attachments?.map((f, idx) => {
                  const {
                    name,
                    extension,
                    url
                  } = f;
                  return <Grid item xs={12} key={idx}>
													{/* Image preview */}
													{IMAGE_FILE_EXTENSIONS.includes(extension.toLocaleLowerCase()) && <img src={url} alt={name} style={{
                      maxWidth: "100%",
                      borderRadius: 4
                    }} />}

													{/* Video preview */}
													{VIDEO_FILE_EXTENSIONS.includes(extension.toLocaleLowerCase()) && <video controls style={{
                      maxWidth: "100%",
                      borderRadius: 4
                    }}>
															<source src={url} />
															Your browser does not support the video tag.
														</video>}

													{/* Other files (PDF, DOCX, etc.) */}
													{!IMAGE_FILE_EXTENSIONS.includes(extension.toLocaleLowerCase()) && !VIDEO_FILE_EXTENSIONS.includes(extension.toLocaleLowerCase()) && <Typography variant="body2" component="a" href={url} target="_blank" rel="noopener noreferrer" sx={{
                      textDecoration: "underline",
                      wordBreak: "break-word",
                      color: "primary.main",
                      cursor: "pointer"
                    }}>
																📄 {name}
															</Typography>}
												</Grid>;
                })}
									</Grid>
									<Grid container spacing={1}>
										<Grid item xs={12} alignItems="right" key={"comment_aciton"} sx={{
                  display: "flex",
                  justifyContent: "flex-end"
                }}>
											{c.comment.user === me?._id && <IconButton aria-label="edit_comment" color="primary" onClick={() => handleEditComment(c.comment._id)}>
													<EditNote />
												</IconButton>}
											<IconButton aria-label="reply" color="primary" onClick={() => handleReplyComment(c.comment._id)}>
												<Reply />
											</IconButton>
										</Grid>
									</Grid>
								</Paper>
							</Box>

							{/* Reply Sections */}
							{c.replies.length > 0 && <Accordion sx={{
            boxShadow: "none",
            bgcolor: "transparent",
            "&:before": {
              display: "none"
            },
            width: "100%",
            pl: "8%"
          }}>
									<AccordionSummary sx={{
              flexDirection: "row-reverse"
            }}>
										<Button component="label" variant="outlined" size="small" sx={{
                borderRadius: 20
              }} startIcon={<ExpandCircleDown />}>
											Reply ({c.replies.length})
										</Button>
										<Typography></Typography>
									</AccordionSummary>
									<AccordionDetails>
										{c.replies.map(comment => <Box key={`reply-${comment._id}`} sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 2,
                mb: 2
              }}>
												{/* Avatar */}
												<Avatar sx={{
                  bgcolor: "primary.main",
                  color: "primary.contrastText"
                }}>
													{comment._user.firstName.charAt(0).toUpperCase() || "U"}
												</Avatar>
												<Paper elevation={1} sx={{
                  py: 1,
                  px: 2,
                  borderRadius: 1,
                  backgroundColor: theme => alpha(theme.palette.action.disabled, 0.05),
                  maxWidth: "90%",
                  width: "90%"
                }}>
													{/* Message text */}
													<Typography variant="body2" sx={{
                    mb: comment.attachments?.length ? 1 : 0
                  }}>
														{`${comment._user.lastName} ${comment._user.firstName} - ${$dayjs(comment?.updatedAt).format(DATETIMESTRING_FORMAT)}`}
													</Typography>
													{/* Message text */}
													{comment.text && <Typography variant="body1" sx={{
                    mb: comment.attachments?.length ? 1 : 0
                  }}>
															{comment.text}
														</Typography>}

													{/* Attachments */}
													<Grid container spacing={1}>
														{comment.attachments?.map((f, idx) => {
                      const {
                        name,
                        extension,
                        url
                      } = f;
                      return <Grid item xs={12} key={idx}>
																	{/* Image preview */}
																	{IMAGE_FILE_EXTENSIONS.includes(extension.toLocaleLowerCase()) && <img src={url} alt={name} style={{
                          maxWidth: "100%",
                          borderRadius: 4
                        }} />}

																	{/* Video preview */}
																	{VIDEO_FILE_EXTENSIONS.includes(extension.toLocaleLowerCase()) && <video controls style={{
                          maxWidth: "100%",
                          borderRadius: 4
                        }}>
																			<source src={url} />
																			Your browser does not support the video
																			tag.
																		</video>}

																	{/* Other files (PDF, DOCX, etc.) */}
																	{!IMAGE_FILE_EXTENSIONS.includes(extension.toLocaleLowerCase()) && !VIDEO_FILE_EXTENSIONS.includes(extension.toLocaleLowerCase()) && <Typography variant="body2" href={url} component="a" target="_blank" rel="noopener noreferrer" sx={{
                          textDecoration: "underline",
                          wordBreak: "break-word",
                          color: "primary.main",
                          cursor: "pointer"
                        }}>
																				📄 {name}
																			</Typography>}
																</Grid>;
                    })}
													</Grid>
													<Grid container spacing={1}>
														<Grid item xs={12} alignItems="right" key={"comment_aciton"} sx={{
                      display: "flex",
                      justifyContent: "flex-end"
                    }}>
															{comment.user === me?._id && <IconButton aria-label="edit_comment" color="primary" onClick={() => handleEditComment(comment._id)}>
																	<EditNote />
																</IconButton>}
															<IconButton aria-label="reply" color="primary" onClick={() => handleReplyComment(comment._id)}>
																<Reply />
															</IconButton>
														</Grid>
													</Grid>
												</Paper>
											</Box>)}
									</AccordionDetails>
								</Accordion>}
						</>)}
				</Stack>
			</Box>
			<EditCommentSlider isOpen={isEditCommentSliderOpen} isAttachmentEnabled={isAttachmentEnabled} comment={updatedComment} companyId={company!._id} onClose={closeEditCommentSlider} data-sentry-element="EditCommentSlider" data-sentry-source-file="index.tsx" />
			<ReplyCommentSlider isOpen={isReplyCommentSliderOpen} isAttachmentEnabled={isAttachmentEnabled} file={file} comment={updatedComment} companyId={company!._id} onClose={closeReplyCommentSlider} data-sentry-element="ReplyCommentSlider" data-sentry-source-file="index.tsx" />
		</>;
};
const EditCommentSlider: FC<{
  isOpen: boolean;
  isAttachmentEnabled: boolean | undefined;
  comment: GetCommentsByFileIdResults[0] | undefined;
  companyId: string;
  onClose: () => void;
}> = ({
  isOpen,
  isAttachmentEnabled,
  comment,
  companyId,
  onClose
}) => {
  const updateComment = useUpdateComment();
  const getFileUploadUrl = useGetFileUploadUrl();
  const [updatedComment, setUpdatedComment] = useState<GetCommentsByFileIdResults[0] | undefined>();
  const [files, setFiles] = useState<FileAttachment[]>([]);
  useEffect(() => {
    if (comment) setUpdatedComment(comment);
  }, [comment]);
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!comment || !updatedComment) return;
    if (!updatedComment.text && files.length === 0) return;
    await toast.promise(
    // eslint-disable-next-line no-async-promise-executor
    new Promise(async (resolve, reject) => {
      try {
        const fileAttachments: CommentAttachmentInput[] = [];
        if (files.length > 0) {
          for (const f of files) {
            const {
              name,
              size,
              type
            } = f.file;
            const nameArr = name.split(".");
            const {
              uploadUrl,
              filePath
            } = await getFileUploadUrl.mutateAsync({
              type: FILE_UPLOAD_TYPE.COMMENT_ATTACHMENT,
              id: companyId,
              extension: nameArr[nameArr.length - 1],
              mimeType: type
            });
            await axios.put(uploadUrl, f.file, {
              headers: {
                "Content-Type": type
              },
              transformRequest: data => data
            });
            fileAttachments.push({
              name: name,
              extension: nameArr[nameArr.length - 1],
              size,
              url: filePath
            });
          }
        }
        await updateComment.mutateAsync({
          id: comment._id,
          input: {
            ...(updatedComment.text ? {
              text: updatedComment.text.trim()
            } : undefined),
            ...(fileAttachments.length > 0 ? {
              attachments: comment.attachments ? [...comment.attachments, ...fileAttachments] : fileAttachments
            } : undefined)
          }
        });
        setFiles([]);
        resolve(null);
      } catch (e) {
        reject(e instanceof Error ? e : new Error("Something went wrong"));
      }
    }), {
      loading: "Updating comment...",
      success: "Comment updates successfully",
      error: "Failed to updates comment"
    });
    onClose();
  };
  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files ?? []);
    const newFiles: FileAttachment[] = selectedFiles.map(file => {
      const type = file.type;
      const preview = type.startsWith("image/") || type.startsWith("video/") ? URL.createObjectURL(file) : null;
      return {
        file,
        preview,
        type
      };
    });
    setFiles(prev => [...prev, ...newFiles]);
  };
  const handleRemoveFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };
  return <Drawer anchor="bottom" open={isOpen} onClose={onClose} PaperProps={{
    sx: {
      borderTopLeftRadius: 8,
      borderTopRightRadius: 8,
      padding: 3
    }
  }} ModalProps={{
    sx: {
      zIndex: theme => theme.zIndex.modal + 100 // 1300 + 100 = 1400
    }
  }} data-sentry-element="Drawer" data-sentry-component="EditCommentSlider" data-sentry-source-file="index.tsx">
			<Typography variant="h6" gutterBottom data-sentry-element="Typography" data-sentry-source-file="index.tsx">
				Edit Comment
			</Typography>

			<Box component="form" onSubmit={handleSubmit} noValidate data-sentry-element="Box" data-sentry-source-file="index.tsx">
				<TextField label="Write a comment" multiline fullWidth rows={3} value={updatedComment?.text} onChange={e => {
        if (!updatedComment) return;
        setUpdatedComment({
          ...updatedComment,
          text: e.target.value
        });
      }} variant="outlined" data-sentry-element="TextField" data-sentry-source-file="index.tsx" />

				{/* Preview attachment files */}
				{comment?.attachments && comment?.attachments.length > 0 && <Divider>
						<Typography variant="body2">Previous Attachments</Typography>
					</Divider>}

				{comment?.attachments?.map((f, idx) => {
        const type = "image/";
        const {
          name,
          extension,
          url
        } = f;
        return <Grid item xs={12} key={idx}>
							{/* Image preview */}
							{IMAGE_FILE_EXTENSIONS.includes(extension.toLocaleLowerCase()) && <img
          // src={preview}
          alt={name} style={{
            maxWidth: "100%",
            borderRadius: 4
          }} />}

							{/* Video preview */}
							{VIDEO_FILE_EXTENSIONS.includes(extension.toLocaleLowerCase()) && <video controls style={{
            maxWidth: "100%",
            borderRadius: 4
          }}>
									<source
            // src={preview}
            type={type} />
									Your browser does not support the video tag.
								</video>}

							{/* Other files (PDF, DOCX, etc.) */}
							{!IMAGE_FILE_EXTENSIONS.includes(extension.toLocaleLowerCase()) && !VIDEO_FILE_EXTENSIONS.includes(extension.toLocaleLowerCase()) && <Typography variant="body2" component="a" href={url} target="_blank" rel="noopener noreferrer" sx={{
            textDecoration: "underline",
            wordBreak: "break-word",
            color: "primary.main",
            cursor: "pointer"
          }}>
										📄 {name}
									</Typography>}
						</Grid>;
      })}

				{/* Preview selected files */}
				{files && files.length > 0 && <Divider sx={{
        mt: 2
      }}>
						<Typography variant="body2">New Attachments</Typography>
					</Divider>}
				<Grid container spacing={2} sx={{
        mt: 1
      }} data-sentry-element="Grid" data-sentry-source-file="index.tsx">
					{files.map((f, idx) => <Grid item xs={6} sm={4} key={idx}>
							<Box sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            border: "1px solid #ccc",
            borderRadius: 2,
            p: 1
          }}>
								{/* File preview */}
								{f.type.startsWith("image/") && f.preview && <img src={f.preview} alt={f.file.name} style={{
              width: 60,
              height: 60,
              objectFit: "cover"
            }} />}

								{f.type.startsWith("video/") && f.preview && <video width="80" height="60" controls>
										<source src={f.preview} type={f.type} />
									</video>}

								{/* File name display */}
								<Box sx={{
              flexGrow: 1
            }}>
									<Typography variant="body2" sx={{
                wordBreak: "break-word"
              }} title={f.file.name}>
										{f.file.name}
									</Typography>
								</Box>

								<IconButton onClick={() => handleRemoveFile(idx)} color="error">
									<DeleteIcon />
								</IconButton>
							</Box>
						</Grid>)}
				</Grid>

				<Box sx={{
        mt: 2,
        display: "flex",
        justifyContent: "right",
        gap: 2
      }} data-sentry-element="Box" data-sentry-source-file="index.tsx">
					{isAttachmentEnabled && <Button component="label" variant="outlined" startIcon={<AttachFileIcon />}>
							Attach File
							<input type="file" hidden multiple onChange={handleFileChange} />
						</Button>}
					<Button type="submit" variant="contained" disabled={!updatedComment?.text} data-sentry-element="Button" data-sentry-source-file="index.tsx">
						Update
					</Button>
				</Box>
			</Box>
		</Drawer>;
};
const ReplyCommentSlider: FC<{
  isOpen: boolean;
  isAttachmentEnabled: boolean | undefined;
  file: GetFileByIdResults | undefined;
  comment: GetCommentsByFileIdResults[0] | undefined;
  companyId: string;
  onClose: () => void;
}> = ({
  isOpen,
  isAttachmentEnabled,
  file,
  comment,
  companyId,
  onClose
}) => {
  const createComment = useCreateComment();
  const getFileUploadUrl = useGetFileUploadUrl();
  const [input, setInput] = useState<string>("");
  const [files, setFiles] = useState<FileAttachment[]>([]);
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!input.trim() && files.length === 0) return;
    if (!file || !comment) return;
    await toast.promise(
    // eslint-disable-next-line no-async-promise-executor
    new Promise(async (resolve, reject) => {
      try {
        const fileAttachments: CommentAttachmentInput[] = [];
        if (files.length > 0) {
          for (const f of files) {
            const {
              name,
              size,
              type
            } = f.file;
            const nameArr = name.split(".");
            const {
              uploadUrl,
              filePath
            } = await getFileUploadUrl.mutateAsync({
              type: FILE_UPLOAD_TYPE.COMMENT_ATTACHMENT,
              id: companyId,
              extension: nameArr[nameArr.length - 1],
              mimeType: type
            });
            await axios.put(uploadUrl, f.file, {
              headers: {
                "Content-Type": type
              },
              transformRequest: data => data
            });
            fileAttachments.push({
              name: name,
              extension: nameArr[nameArr.length - 1],
              size,
              url: filePath
            });
          }
        }
        await createComment.mutateAsync({
          input: {
            text: input.trim(),
            file: file._id,
            ...(fileAttachments.length > 0 ? {
              attachments: fileAttachments
            } : undefined),
            comment: comment.comment ? comment.comment : comment._id
          }
        });
        resolve(null);
      } catch (e) {
        reject(e instanceof Error ? e : new Error("Something went wrong"));
      }
    }), {
      loading: "Uploading comment...",
      success: "Comment uploads successfully",
      error: "Failed to upload comment"
    });
    setInput("");
    setFiles([]);
    onClose();
  };
  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files ?? []);
    const newFiles: FileAttachment[] = selectedFiles.map(file => {
      const type = file.type;
      const preview = type.startsWith("image/") || type.startsWith("video/") ? URL.createObjectURL(file) : null;
      return {
        file,
        preview,
        type
      };
    });
    setFiles(prev => [...prev, ...newFiles]);
  };
  const handleRemoveFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };
  return <Drawer anchor="bottom" open={isOpen} onClose={onClose} PaperProps={{
    sx: {
      borderTopLeftRadius: 8,
      borderTopRightRadius: 8,
      padding: 3
    }
  }} ModalProps={{
    sx: {
      zIndex: theme => theme.zIndex.modal + 100 // 1300 + 100 = 1400
    }
  }} data-sentry-element="Drawer" data-sentry-component="ReplyCommentSlider" data-sentry-source-file="index.tsx">
			<Typography variant="h6" mb={2} gutterBottom data-sentry-element="Typography" data-sentry-source-file="index.tsx">
				Reply to {`${comment?._user.lastName} ${comment?._user?.firstName}`}
			</Typography>

			<Box sx={{
      display: "flex",
      alignItems: "center",
      backgroundColor: theme => alpha(theme.palette.action.disabled, 0.05),
      borderRadius: 1,
      p: 1,
      mb: 1
    }} data-sentry-element="Box" data-sentry-source-file="index.tsx">
				{/* Avatar */}
				<Avatar sx={{
        bgcolor: "primary.main",
        color: "primary.contrastText",
        mr: 2
      }} data-sentry-element="Avatar" data-sentry-source-file="index.tsx">
					{comment?._user.firstName.charAt(0).toUpperCase() ?? "U"}
				</Avatar>
				<Box sx={{
        flex: 1,
        overflow: "hidden"
      }} data-sentry-element="Box" data-sentry-source-file="index.tsx">
					<Typography variant="body1" noWrap sx={{
          maxWidth: "100%"
        }} data-sentry-element="Typography" data-sentry-source-file="index.tsx">
						{comment?.text}
					</Typography>
				</Box>
			</Box>

			<Box component="form" onSubmit={handleSubmit} noValidate data-sentry-element="Box" data-sentry-source-file="index.tsx">
				<TextField label="Write a comment" multiline fullWidth rows={3} value={input} onChange={e => {
        setInput(e.target.value);
      }} variant="outlined" data-sentry-element="TextField" data-sentry-source-file="index.tsx" />

				{/* Preview selected files */}
				<Grid container spacing={2} sx={{
        mt: 2
      }} data-sentry-element="Grid" data-sentry-source-file="index.tsx">
					{files.map((f, idx) => <Grid item xs={12} sm={6} key={idx}>
							<Box sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            border: "1px solid #ccc",
            borderRadius: 2,
            p: 1
          }}>
								{/* File preview */}
								{f.type.startsWith("image/") && f.preview && <img src={f.preview} alt={f.file.name} style={{
              width: 60,
              height: 60,
              objectFit: "cover"
            }} />}

								{f.type.startsWith("video/") && f.preview && <video width="80" height="60" controls>
										<source src={f.preview} type={f.type} />
									</video>}

								{/* File name display */}
								<Box sx={{
              flexGrow: 1
            }}>
									<Typography variant="body2" sx={{
                wordBreak: "break-word"
              }} title={f.file.name}>
										{f.file.name}
									</Typography>
								</Box>

								<IconButton onClick={() => handleRemoveFile(idx)} color="error">
									<DeleteIcon />
								</IconButton>
							</Box>
						</Grid>)}
				</Grid>

				<Box sx={{
        mt: 2,
        display: "flex",
        justifyContent: "right",
        gap: 2
      }} data-sentry-element="Box" data-sentry-source-file="index.tsx">
					{isAttachmentEnabled && <Button component="label" variant="outlined" startIcon={<AttachFileIcon />}>
							Attach File
							<input type="file" hidden multiple onChange={handleFileChange} />
						</Button>}
					<Button type="submit" variant="contained" disabled={!input.trim() && files.length === 0} data-sentry-element="Button" data-sentry-source-file="index.tsx">
						Comment
					</Button>
				</Box>
			</Box>
		</Drawer>;
};
export default CommentSection;